/**
 * opora-ask-client.js — shared Ask Opora client (JSON + SSE)
 * Handles worker envelopes: { ok, data: { answer, citations } } and SSE content/delta chunks.
 */
(function (global) {
  'use strict';

  function apiBase() {
    if (global.OporaEdge && global.OporaEdge.WORKER_URL) return global.OporaEdge.WORKER_URL;
    return (global.OPORA_WORKER_URL || 'https://api.oporasupply.com').replace(/\/$/, '');
  }

  /* The fallback host only serves the legacy /api/ask routes — /api/edge/* 404s there. */
  var FALLBACK_PATHS = {
    '/api/edge/ask': '/api/ask',
    '/api/edge/ask-deep': '/api/ask-deep'
  };

  function fetchAsk(path, opts) {
    if (global.OporaEdge && typeof global.OporaEdge.fetchApi === 'function') {
      return global.OporaEdge.fetchApi(path, opts);
    }
    var fallback = (global.OPORA_WORKER_URL_FALLBACK || 'https://opora-ai.mshabliy.workers.dev').replace(/\/$/, '');
    var abs = path.charAt(0) === '/' ? path : '/' + path;
    var fallbackPath = FALLBACK_PATHS[abs] || (abs.indexOf('/api/edge/') === 0 ? '' : abs);
    return fetch(apiBase() + abs, opts).catch(function (err) {
      if (!fallbackPath) throw err;
      return fetch(fallback + fallbackPath, opts);
    });
  }

  function buildBody(question, opts) {
    opts = opts || {};
    var body = {
      question: String(question || '').trim(),
      history: opts.history || [],
      mode: opts.mode || 'fast',
      deep: !!opts.deep
    };
    if (global.OporaProfile && typeof global.OporaProfile.get === 'function') {
      body.profile = global.OporaProfile.get();
    }
    if (global.OporaSpine && typeof global.OporaSpine.getThreadContext === 'function') {
      body.pipeline_context = global.OporaSpine.getThreadContext();
    }
    if (global.OporaGeo && typeof global.OporaGeo.enrichBody === 'function') {
      body = global.OporaGeo.enrichBody(body, question);
    }
    if (!opts.deep && global.OporaGeo && global.OporaGeo.isComplexQuestion && global.OporaGeo.isComplexQuestion(question)) {
      opts.deep = true;
    }
    if (!opts.deep && global.OporaGeo && global.OporaGeo.extract && global.OporaGeo.extract(question)) {
      opts._useGeoRoute = true;
    }
    if (opts.profile) body.profile = Object.assign({}, body.profile, opts.profile);
    return body;
  }

  function unwrapEnvelope(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    if (obj.ok === true && obj.data && typeof obj.data === 'object') return obj.data;
    return obj;
  }

  function extractText(obj) {
    if (!obj || typeof obj !== 'object') return '';
    obj = unwrapEnvelope(obj);
    var t = obj.text || obj.content || obj.answer || obj.delta || obj.token || obj.chunk;
    if (!t && obj.choices && obj.choices[0]) {
      var c0 = obj.choices[0];
      t = (c0.delta && c0.delta.content) || c0.text || (c0.message && c0.message.content);
    }
    return typeof t === 'string' ? t : '';
  }

  function extractCitations(obj) {
    if (!obj || typeof obj !== 'object') return [];
    obj = unwrapEnvelope(obj);
    var c = obj.citations || obj.sources || obj.references;
    return Array.isArray(c) ? c : [];
  }

  function normalizeCitation(c) {
    if (!c) return null;
    if (typeof c === 'string') return { title: c, name: c, url: '' };
    return {
      title: c.title || c.name || c.source || '',
      name: c.title || c.name || c.source || c.domain || '',
      url: c.url || '',
      snippet: c.snippet || c.detail || '',
      source_type: c.source_type || c.type || 'reference'
    };
  }

  function parseSseStream(res, onChunk) {
    var fullText = '';
    var citations = [];
    var meta = {};
    var rawAcc = '';

    function absorb(obj) {
      if (!obj) return;
      if (typeof obj === 'string') {
        fullText += obj;
        if (onChunk) onChunk(fullText, citations.slice());
        return;
      }
      var t = extractText(obj);
      if (t) {
        fullText += t;
        if (onChunk) onChunk(fullText, citations.slice());
      }
      extractCitations(obj).forEach(function (c) {
        var n = normalizeCitation(c);
        if (!n || !n.name) return;
        for (var i = 0; i < citations.length; i++) {
          if (citations[i].url && n.url && citations[i].url === n.url) return;
          if (citations[i].name === n.name) return;
        }
        citations.push(n);
      });
      if (obj.routing) meta.routing = obj.routing;
      if (obj.geo_state) meta.geo_state = obj.geo_state;
      if (obj.detected_states) meta.detected_states = obj.detected_states;
      if (typeof obj.is_comparative === 'boolean') meta.is_comparative = obj.is_comparative;
      if (obj.intent) meta.intent = obj.intent;
      if (obj.pipeline_intent) meta.pipeline_intent = obj.pipeline_intent;
      if (obj.ctas) meta.ctas = obj.ctas;
      if (obj.derived_followups) meta.derived_followups = obj.derived_followups;
      if (obj.compiler_state) meta.compiler_state = obj.compiler_state;
      if (obj.engine_result) meta.engine_result = obj.engine_result;
      if (obj.validation_errors) meta.validation_errors = obj.validation_errors;
    }

    function processLine(line) {
      line = line.trim();
      if (!line || line === 'data: [DONE]' || line === '[DONE]') return;
      if (line.indexOf('event:') === 0 || line.indexOf('id:') === 0) return;
      var payload = line.indexOf('data:') === 0 ? line.slice(5).trim() : line;
      if (!payload) return;
      if (payload.charAt(0) === '{' || payload.charAt(0) === '[') {
        try { absorb(JSON.parse(payload)); return; } catch (e) { /* fall through */ }
      }
      fullText += payload;
      if (onChunk) onChunk(fullText, citations.slice());
    }

    if (!res.body || !res.body.getReader) {
      return res.text().then(function (t) {
        rawAcc = t;
        t.split('\n').forEach(processLine);
        if (!fullText.trim()) {
          try { absorb(JSON.parse(t)); } catch (e) {}
        }
        return { answer: fullText.trim(), citations: citations, meta: meta };
      });
    }

    var reader = res.body.getReader();
    var decoder = new TextDecoder();
    var buffer = '';

    function flush(isEnd) {
      var parts = buffer.split('\n');
      buffer = isEnd ? '' : parts.pop();
      parts.forEach(processLine);
    }

    function pump() {
      return reader.read().then(function (r) {
        if (r.value) {
          var chunk = decoder.decode(r.value, { stream: true });
          rawAcc += chunk;
          buffer += chunk;
          flush(false);
        }
        if (r.done) {
          flush(true);
          if (!fullText.trim() && rawAcc.trim()) {
            try { absorb(JSON.parse(rawAcc.trim())); } catch (e) {
              rawAcc.split('\n').forEach(processLine);
            }
          }
          return { answer: fullText.trim(), citations: citations, meta: meta };
        }
        return pump();
      });
    }

    return pump();
  }

  function ask(question, opts) {
    opts = opts || {};
    var body = buildBody(question, opts);
    var path = opts.deep ? '/api/edge/ask-deep' : '/api/edge/ask';

    return fetchAsk(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': opts.preferJson ? 'application/json' : 'text/event-stream, application/json'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      var ct = (res.headers.get('content-type') || '').toLowerCase();

      if (!res.ok) {
        return res.text().then(function (t) {
          var msg = 'Request failed';
          try {
            var j = JSON.parse(t);
            msg = (j.error && (j.error.message || j.error.code)) || j.message || msg;
          } catch (e) {
            if (t) msg = t.slice(0, 200);
          }
          var err = new Error(msg);
          err.status = res.status;
          throw err;
        });
      }

      if (ct.indexOf('text/event-stream') !== -1) {
        return parseSseStream(res, opts.onChunk);
      }

      return res.json().then(function (envelope) {
        if (envelope.ok === false) {
          var emsg = (envelope.error && envelope.error.message) || 'Ask Opora could not answer that question.';
          var err = new Error(emsg);
          err.status = res.status;
          throw err;
        }
        var data = unwrapEnvelope(envelope);
        var answer = data.answer || data.text || data.content || '';
        var citations = extractCitations(data).map(normalizeCitation).filter(Boolean);
        if (opts.onChunk && answer) opts.onChunk(answer, citations);
        return {
          answer: answer,
          citations: citations,
          meta: {
            routing: data.routing || res.headers.get('X-Opora-Routing') || '',
            geo_state: data.geo_state || res.headers.get('X-Opora-Geo-State') || '',
            detected_states: Array.isArray(data.detected_states) ? data.detected_states : [],
            is_comparative: !!data.is_comparative,
            intent: data.intent || res.headers.get('X-Opora-Intent') || '',
            pipeline_intent: data.pipeline_intent || res.headers.get('X-Opora-Pipeline-Intent') || '',
            ctas: Array.isArray(data.ctas) ? data.ctas : [],
            derived_followups: data.derived_followups || [],
            compiler_state: data.compiler_state || null,
            engine_result: data.engine_result || null,
            validation_errors: data.validation_errors || null
          }
        };
      });
    });
  }

  global.OporaAsk = {
    ask: ask,
    buildBody: buildBody,
    parseSseStream: parseSseStream,
    unwrapEnvelope: unwrapEnvelope
  };

})(window);
